import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from '../jsx/components/common/Toast/toast';
import { getUsersByIds } from '../services/AuthService';
import { useAuth } from './AuthContext';
/* One sound for everything that arrives. This used to synthesise an
   880Hz sine wave here while the notification bell played a different
   file of its own -- same class of event, two different noises. Both now
   come from utils/notificationSound. */
import { playNotificationSound, primeNotificationSound } from '../utils/notificationSound';

const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const activeChatRef = useRef(null);
  // Messages already announced, so one the server sends twice is shown and
  // heard once.
  const announcedMessagesRef = useRef(new Set());

  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [usersMap, setUsersMap] = useState({});

  /* Fetch and decode the clip now, while nothing is waiting on it. The
     provider wraps the whole signed-in app, so this is the earliest
     point that runs once and only for a real session. */
  useEffect(() => {
    primeNotificationSound();
  }, []);

  /* ------------------ Track Active Chat ------------------ */
  const setGlobalActiveChatId = (chatId) => {
    activeChatRef.current = chatId;
  };

  /* ------------------ Init Socket ------------------ */
  useEffect(() => {
    // If not authenticated, ensure socket is disconnected and return
    if (!isAuthenticated || !user) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // If socket already exists and is connected, we might not need to do anything
    // But if the token changed (e.g. login as different user), we should reconnect.
    // For simplicity, we can disconnect existing and connect new.
    if (socket) {
      socket.disconnect();
    }

    const socketUrl = process.env.REACT_APP_CHAT_API_URL || 'https://chat-api.sevenmentor.io';

    const newSocket = io(socketUrl, {
      path: '/socket.io',
      withCredentials: true,
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
    });

    setSocket(newSocket);

    newSocket.on('connect', () => {
      setIsConnected(true);
      /* Presence starts from nothing on every (re)connect. The server
         replays everyone online straight after the handshake
         (chat.socket.js), so this loses nobody -- and without it, a
         user_offline missed while the socket was down left that person
         "online" here for the rest of the session. */
      setOnlineUsers(new Set());
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    /* ------------------ Presence ------------------ */
    newSocket.on('user_online', (userId) => {
      setOnlineUsers((prev) => new Set(prev).add(userId));
    });

    newSocket.on('user_offline', (userId) => {
      setOnlineUsers((prev) => {
        const copy = new Set(prev);
        copy.delete(userId);
        return copy;
      });
    });

    /* ------------------ Typing ------------------ */
    newSocket.on('typing', ({ chatId, userId }) => {
      setTypingUsers((prev) => ({
        ...prev,
        [chatId]: userId,
      }));
    });

    newSocket.on('stop_typing', ({ chatId }) => {
      setTypingUsers((prev) => {
        const copy = { ...prev };
        delete copy[chatId];
        return copy;
      });
    });

    /* ------------------ Unread Count ------------------ */
    newSocket.on('unread_count', ({ chatId, count }) => {
      setUnreadCounts((prev) => ({
        ...prev,
        [chatId]: count,
      }));
    });

    /* ------------------ Messages ------------------ */
    newSocket.on('receive_message', async (message) => {
      const chatId = message.chatId;
      const currentUser = JSON.parse(localStorage.getItem('userDetails') || '{}');

      if (message.senderId === currentUser.id) return;

      const messageId = message.id || message._id;
      if (messageId) {
        if (announcedMessagesRef.current.has(messageId)) return;
        announcedMessagesRef.current.add(messageId);
      }

      playNotificationSound();

      if (activeChatRef.current !== chatId) {
        setUnreadCounts((prev) => ({
          ...prev,
          [chatId]: (prev[chatId] || 0) + 1,
        }));
      }

      let senderName = 'New Message';
      if (!usersMap[message.senderId]) {
        const users = await getUsersByIds([message.senderId]);
        if (users?.length) {
          setUsersMap((p) => ({ ...p, [users[0].id]: users[0] }));
          senderName = users[0].firstName;
        }
      }

      toast.info(`${senderName}: ${message.content}`);
    });

    return () => {
      newSocket.disconnect();
      setSocket(null);
    };
  }, [isAuthenticated, user]);

  /* ------------------ TOKEN REFRESH (NO RECONNECT) ------------------ */
  const refreshSocketToken = (newToken) => {
    if (!socket) return;
    socket.auth.token = newToken;
    socket.emit('refresh_token', { token: newToken });
  };

  /* ------------------ LOGOUT (AUTO DISCONNECT) ------------------ */
  const disconnectSocket = () => {
    socket?.disconnect();
    setSocket(null);
    setOnlineUsers(new Set());
    setTypingUsers({});
    setUnreadCounts({});
  };

  /* ------------------ EMITTER HELPERS ------------------ */
  const emitTyping = (chatId) => socket?.emit('typing', { chatId });

  const emitStopTyping = (chatId) => socket?.emit('stop_typing', { chatId });

  const markChatRead = (chatId) => {
    setUnreadCounts((prev) => ({ ...prev, [chatId]: 0 }));
    socket?.emit('mark_read', { chatId });
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        onlineUsers,
        typingUsers,
        unreadCounts,
        setGlobalActiveChatId,
        emitTyping,
        emitStopTyping,
        markChatRead,
        refreshSocketToken,
        disconnectSocket,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
